/**
 * NoteReference — a custom inline TipTap node for embedded note references.
 *
 * Renders as a small inline chip:  📝 Note title
 * Clicking navigates to the note detail page.
 *
 * Attributes stored in the document:
 *   - id:    the note's database ID
 *   - title: the note title text
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { NoteReferenceView } from './NoteReferenceView';

export interface NoteReferenceAttrs {
  id: string | null;
  title: string | null;
}

export const NoteReference = Node.create({
  name: 'noteReference',

  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-note-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) return {};
          return { 'data-note-id': attributes.id };
        },
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-note-title'),
        renderHTML: (attributes) => {
          if (!attributes.title) return {};
          return { 'data-note-title': attributes.title };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="noteReference"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          'data-type': 'noteReference',
          class: 'note-reference-node',
        },
        HTMLAttributes,
      ),
      HTMLAttributes['data-note-title'] || 'Untitled note',
    ];
  },

  renderText({ node }) {
    return `📝 ${node.attrs.title || 'Untitled note'}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(NoteReferenceView, {
      as: 'span',
      className: 'note-reference-nodeview-wrapper',
    });
  },
});
